import React, { useState, useEffect, useRef } from "react";
import {
  useEnrollment,
  EnrollmentResponseDto,
} from "../../hooks/useEnrollment";
import { useSessionClass } from "contexts/SessionClassContext";
import { useAuth } from "contexts/AuthContext";
import { useMedium } from "components/layout/Layout";
import { Alert, Box, Typography, CircularProgress, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSchools } from "../../hooks/useSchools";
import { useSubjects } from "../../hooks/useSubjects";
import html2canvas from "html2canvas";
import { Download } from "@mui/icons-material";


const EnrolmentTemplate: React.FC = () => {
  const { t } = useTranslation();
  const { selectedClass, selectedSession } = useSessionClass();
  const { selectedMedium } = useMedium();
  const { user } = useAuth();
  const schoolId = user?.schoolId;
  const classId = selectedClass?.id;
  const sessionId = selectedSession?.id;
  

  const { enrollmentMutation } = useEnrollment();
  const [data, setData] = useState<EnrollmentResponseDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [schoolLogoUrl, setSchoolLogoUrl] = useState<string | null>(null);
  const templateRef = useRef<HTMLDivElement>(null);

  const { useSchoolById, useSchoolLogoInfo } = useSchools();
  const { data: schoolData, isLoading: schoolLoading } = useSchoolById(
    schoolId ?? undefined
  );

  // Fetch school logo info
  const { data: logoInfo, isLoading: logoLoading } = useSchoolLogoInfo(schoolId ?? 1);

  const { subjectsQuery } = useSubjects();
  const { data: subjectsData, isLoading: subjectsLoading } = subjectsQuery;

  // Function to get subject code by name
  const getSubjectCode = (subjectName: string): string | null => {
    if (!subjectsData) return null;
    const subject = subjectsData.find(s => s.name.toLowerCase() === subjectName.toLowerCase());
    return subject?.subjectCode || null;
  };

  // Function to download template as image
  const downloadAsImage = async () => {
    if (!templateRef.current) return;
    
    setIsDownloading(true);
    try {
      templateRef.current.classList.add("download-mode");
      
      // Find only sub-column "TOTAL" cells (not the main header TOTAL)
      const allCells = templateRef.current.querySelectorAll('th');
      const subTotalCells: HTMLElement[] = [];
      
      allCells.forEach((cell) => {
        const htmlCell = cell as HTMLElement;
        const isTotal = htmlCell.textContent?.trim() === 'TOTAL' || 
                       htmlCell.textContent?.trim() === t('total');
        
        // Check if this is NOT the main header TOTAL (which has colSpan)
        const isMainHeader = htmlCell.getAttribute('colspan') !== null;
        
        if (isTotal && !isMainHeader) {
          subTotalCells.push(htmlCell);
        }
      });
      
      // Store original styles and apply vertical stacked format for export
      const originalStyles: any[] = [];
      subTotalCells.forEach((cell: HTMLElement) => {
        // Store original content and styles
        const originalContent = cell.innerHTML;
        originalStyles.push({
          element: cell,
          content: originalContent,
          writingMode: cell.style.writingMode,
          textOrientation: cell.style.textOrientation,
          transform: cell.style.transform,
          textAlign: cell.style.textAlign,
          verticalAlign: cell.style.verticalAlign,
          whiteSpace: cell.style.whiteSpace,
          minWidth: cell.style.minWidth,
          height: cell.style.height,
          display: cell.style.display,
          alignItems: cell.style.alignItems,
          justifyContent: cell.style.justifyContent,
          flexDirection: cell.style.flexDirection
        });
        
        // Create vertical stacked format with each letter rotated 90° clockwise and compact spacing
        cell.innerHTML = `
          <div style="
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            height: 100%;
            gap: 0px;
            line-height: 1;
            letter-spacing: -1px;
          ">
            <div style="transform: rotate(90deg); font-weight: bold; line-height: 0.8; margin: -2px 0;">T</div>
            <div style="transform: rotate(90deg); font-weight: bold; line-height: 0.8; margin: -2px 0;">O</div>
            <div style="transform: rotate(90deg); font-weight: bold; line-height: 0.8; margin: -2px 0;">T</div>
            <div style="transform: rotate(90deg); font-weight: bold; line-height: 0.8; margin: -2px 0;">A</div>
            <div style="transform: rotate(90deg); font-weight: bold; line-height: 0.8; margin: -2px 0;">L</div>
          </div>
        `;
        
        // Apply container styles
        cell.style.writingMode = 'horizontal-tb';
        cell.style.textOrientation = 'mixed';
        cell.style.transform = 'none';
        cell.style.textAlign = 'center';
        cell.style.verticalAlign = 'middle';
        cell.style.whiteSpace = 'nowrap';
        cell.style.minWidth = '30px';
        cell.style.height = '80px';
        cell.style.display = 'flex';
        cell.style.alignItems = 'center';
        cell.style.justifyContent = 'center';
        cell.style.flexDirection = 'column';
      });
      
      // Wait a bit to ensure all styles are applied
      await new Promise((resolve) => setTimeout(resolve, 100));
      
      const canvas = await html2canvas(templateRef.current, {
        useCORS: true,
        allowTaint: true,
        backgroundColor: null, // Use null to preserve actual background colors and gradients
        scale: 2, // Higher scale for better quality
        logging: false,
        imageTimeout: 15000,
        removeContainer: false,
        onclone: (clonedDoc: Document) => {
          // Set white background only on the outermost container to preserve all inner colors
          const clonedElement = clonedDoc.body.firstElementChild as HTMLElement;
          if (clonedElement) {
            // Set white background on the outer div while preserving all inner element colors
            clonedElement.style.backgroundColor = '#ffffff';
          }
        },
      } as any);
      
      // Restore original styles and content
      originalStyles.forEach(({ element, content, ...styles }) => {
        element.innerHTML = content;
        element.style.writingMode = styles.writingMode;
        element.style.textOrientation = styles.textOrientation;
        element.style.transform = styles.transform;
        element.style.textAlign = styles.textAlign;
        element.style.verticalAlign = styles.verticalAlign;
        element.style.whiteSpace = styles.whiteSpace;
        element.style.minWidth = styles.minWidth;
        element.style.height = styles.height;
        element.style.display = styles.display;
        element.style.alignItems = styles.alignItems;
        element.style.justifyContent = styles.justifyContent;
        element.style.flexDirection = styles.flexDirection;
      });
      
      const link = document.createElement('a');
      link.download = `enrollment-template-${data?.className}-${data?.session}-${new Date().toISOString().split('T')[0]}.png`;
      link.href = canvas.toDataURL('image/png', 1.0);
      link.click();
    } catch (error) {
      setError(t('failed_to_download_image'));
    } finally {
      templateRef.current.classList.remove("download-mode");
    setIsDownloading(false);
    }
  };


  // Effect to handle logo fetching with localStorage caching
  useEffect(() => {
    const fetchSchoolLogo = async () => {
      try {
        // If we have logoInfo, always use the latest logo (don't rely on cache for updates)
        if (logoInfo && logoInfo.fileName) {
          const logoUrl = `${process.env.REACT_APP_API_URL}/schools/logos/${logoInfo.fileName}`;
          setSchoolLogoUrl(logoUrl);

          // Update cache with latest logo
          const cachedLogoKey = `school_logo_${schoolId}`;
          localStorage.setItem(cachedLogoKey, logoUrl);
          return;
        }

        // Fallback: check localStorage for cached logo only if no logoInfo
        const cachedLogoKey = `school_logo_${schoolId}`;
        const cachedLogo = localStorage.getItem(cachedLogoKey);

        if (cachedLogo) {
          setSchoolLogoUrl(cachedLogo);
          return;
        }

        // Final fallback to default logo
        setSchoolLogoUrl('/logo.png');
      } catch (error) {
        // Fallback to default logo
        setSchoolLogoUrl('/logo.png');
      }
    };

    if (schoolId && !logoLoading) {
      fetchSchoolLogo();
    }
  }, [schoolId, logoInfo, logoLoading]);

  useEffect(() => {
    setError(null);
    const isAllClassForApi =
      selectedClass?.section === "__GRADE_ALL__" ||
      selectedClass?.section === "__CLASS_ALL__" ||
      String(selectedClass?.name || "").includes("-ALL");

    const allClassNameForApi =
      !isAllClassForApi
        ? ""
        : selectedClass?.section === "__CLASS_ALL__"
          ? "Class-ALL"
          : String(selectedClass?.name || "").includes("-ALL")
            ? String(selectedClass?.name || "")
            : selectedClass?.name
              ? `${selectedClass.name}-ALL`
              : "";

    const effectiveClassId =
      !isAllClassForApi && typeof classId === "number" && classId > 0
        ? classId
        : undefined;

    if (schoolId && sessionId && (effectiveClassId || allClassNameForApi)) {
      enrollmentMutation
        .mutateAsync({
          schoolId,
          ...(effectiveClassId ? { classId: effectiveClassId } : {}),
          className: isAllClassForApi ? allClassNameForApi : (selectedClass?.name ?? ""),
          sessionId,
          medium: selectedMedium
        })
        .then(setData)
        .catch((err) => {
          // Try to get backend error message
          let msg = t("an_error_occurred");
          if (err?.response?.data?.message) {
            msg = err.response.data.message;
          } else if (err?.message) {
            msg = err.message;
          }
          setError(msg);
        });
    }
  }, [schoolId, classId, sessionId, selectedMedium, selectedClass?.name, selectedClass?.section]);

  if (!selectedClass || !selectedSession) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info">{t("please_select_class_and_session")}</Alert>
      </Box>
    );
  }

  if (!selectedMedium) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info">{t("please_select_medium")}</Alert>
      </Box>
    );
  }

  // Show loader while fetching data
  if (enrollmentMutation.isLoading || schoolLoading || subjectsLoading) {
    return (
      <Box 
        sx={{ 
          display: "flex", 
          justifyContent: "center", 
          alignItems: "center", 
          minHeight: "50vh",
          flexDirection: "column",
          gap: 2
        }}
      >
        <CircularProgress size={60} color="primary" />
        <Typography variant="h6" color="text.secondary">
          {enrollmentMutation.isLoading ? t("fetching_enrollment_data") : 
           schoolLoading ? t("loading_school_information") : 
           t("loading_subjects_information")}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const boxStyle: React.CSSProperties = {
    background: "white",
    minHeight: "100vh",
    padding: "0",
    fontFamily: "Calibri, Arial, sans-serif",
  };

  // Calculate minimum height based on logo size (logo is 60% width)
  // Logo minimum height should be approximately equal to its width for square logos
  // With maxWidth of 1200px, logo width would be ~720px, so min height should accommodate that
  const logoMinHeight = 700; // Minimum height to properly display the logo watermark
  
  // Calculate dynamic height based on number of subjects
  const subjectRowsCount = data ? Object.keys(data.subjects).length : 0;
  const subjectTableHeight = 50 + (subjectRowsCount * 40); // Header (50px) + rows (40px each)
  
  // Base content height (header + caste table + spacing)
  const baseContentHeight = 250;
  
  // Total minimum height: logo height or content + subjects, whichever is larger
  const calculatedMinHeight = Math.max(
    logoMinHeight,
    baseContentHeight + subjectTableHeight
  );

  const mainBoxStyle: React.CSSProperties = {
    background: "#fff",
    border: "1px solid #e0c7b1",
    margin: "30px auto",
    width: "95%",
    maxWidth: "1200px",
    minHeight: `${calculatedMinHeight}px`, // Set minimum height based on logo and content
    boxShadow: "0 0 10px #d1b6e1",
    padding: "24px", // Equal padding on all sides
    overflow: "hidden",
    boxSizing: "border-box",
    position: "relative", // Add relative positioning for absolute positioned logo
  };

  const headerStyle: React.CSSProperties = {
    marginBottom: "8px",
  };

  const schoolTitleStyle: React.CSSProperties = {
    fontFamily: '"Playfair Display", "Georgia", "Times New Roman", serif',
    fontWeight: "bold",
    fontSize: "3rem",
    letterSpacing: "1px",
  };

  const infoBlockStyle: React.CSSProperties = {
    fontSize: "1.2rem",
    marginTop: "4px",
    lineHeight: 1.4,
    fontWeight: "bold",
  };

  const mediumBoxStyle: React.CSSProperties = {
    background: "#fff3e6",
    border: "1px solid #e0c7b1",
    borderRadius: "8px",
    padding: "8px 32px",
    textAlign: "center",
    margin: "0 auto 8px auto",
    width: "180px",
  };

  const mediumLabelStyle: React.CSSProperties = {
    color: "#ff9900",
    fontWeight: "bold",
    fontSize: "1.5rem",
    letterSpacing: "2px",
  };

  const hindiStyle: React.CSSProperties = {
    color: "#3a5ca8",
    fontWeight: "bold",
    fontSize: "1.3rem",
    marginTop: "4px",
  };

  const tableHeaderStyle: React.CSSProperties = {
    background: "#fbe3d2",
    fontWeight: "bold",
    textAlign: "center",
    border: "1px solid #bdbdbd",
    fontSize: "1rem",
    padding: "4px",
  };

  const totalHeaderStyle: React.CSSProperties = {
    background: "#fbe3d2",
    fontWeight: "bold",
    textAlign: "center",
    border: "1px solid #bdbdbd",
    fontSize: "1rem",
    padding: "8px 4px",
    writingMode: "vertical-rl",
    textOrientation: "mixed",
    minWidth: "40px",
    height: "60px",
  };

  const tableCellStyle: React.CSSProperties = {
    border: "1px solid #bdbdbd",
    textAlign: "center",
    fontSize: "1rem",
    padding: "4px",
  };

  const subjectTableHeader: React.CSSProperties = {
    background: "#fbe3d2",
    fontWeight: "bold",
    textAlign: "left",
    border: "1px solid #bdbdbd",
    fontSize: "1.1rem",
    padding: "4px",
  };

  const subjectTableCell: React.CSSProperties = {
    border: "1px solid #bdbdbd",
    textAlign: "left",
    fontSize: "1rem",
    padding: "4px",
    wordWrap: "break-word",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };


  return (
    <div style={boxStyle}>
      <div ref={templateRef} style={mainBoxStyle}>
        {/* Transparent school logo background */}
        {schoolLogoUrl && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 0,
              pointerEvents: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img
              src={schoolLogoUrl}
              alt="School Logo"
              style={{
                width: "60%",
                height: "auto",
                opacity: 0.2, // Very low opacity for subtle watermark effect
                objectFit: "contain",
                // filter: "grayscale(100%) brightness(1.2)", // Make it more subtle
              }}
            />
          </div>
        )}
        
        {/* Vertical gradient color bands - greenish, yellowish, reddish */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 0,
            pointerEvents: "none",
            background: "linear-gradient(to right, rgba(144, 238, 144, 0.15) 0%, rgba(255, 255, 153, 0.15) 50%, rgba(255, 160, 160, 0.15) 100%)",
          }}
        />
        
        {/* Content wrapper to ensure content appears above background logo */}
        <div style={{ 
          position: "relative", 
          zIndex: 1, 
          backgroundColor: "transparent",
          minHeight: `${calculatedMinHeight}px`, // Ensure content wrapper matches minimum height
          paddingBottom: "24px" // Add bottom padding for spacing
        }}>
        {!data ? (
          <Box 
            sx={{ 
              display: "flex", 
              justifyContent: "center", 
              alignItems: "center", 
              minHeight: "40vh",
              flexDirection: "column",
              gap: 2
            }}
          >
            <CircularProgress size={50} color="primary" />
            <Typography variant="h6" color="text.secondary">
              {t("preparing_enrollment_template")}
            </Typography>
          </Box>
        ) : (
          <>
            {/* Header */}
            <div style={headerStyle}>
              {/* School Name - Centered */}
              <div style={{ textAlign: "center", marginBottom: "12px" }}>
                <div style={schoolTitleStyle}>
                  {schoolLoading ? t("loading_school_information") : schoolData?.nameofSchool || t("school")}
                </div>
              </div>

              {/* Information Fields - Three Rows with Aligned Columns (3-3-1 layout) */}
              <div style={{ marginBottom: "8px", width: "100%" }}>
                {/* Use grid layout for perfect column alignment */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "16px", width: "100%" }}>
                  {/* Column 1 */}
                  <div>
                    <div style={infoBlockStyle}>
                      {t("district")}: {schoolData?.district || t("not_available")}
                    </div>
                    <div style={{...infoBlockStyle, marginTop: "8px"}}>
                      {t("dise_code")}: {schoolData?.diseCode || t("not_available")}
                    </div>
                    <div style={{...infoBlockStyle, marginTop: "8px"}}>
                      {t("medium")}: {selectedMedium || t("not_available")}
                    </div>
                  </div>
                  
                  {/* Column 2 */}
                  <div>
                    <div style={infoBlockStyle}>
                      {t("block")}: {schoolData?.blockName || t("not_available")}
                    </div>
                    <div style={{...infoBlockStyle, marginTop: "8px"}}>
                      {t("mpbse_code")}: {schoolData?.schoolCode || t("not_available")}
                    </div>
                  </div>
                  
                  {/* Column 3 */}
                  <div>
                    <div style={infoBlockStyle}>
                      {t("class")}: {data.className}
                    </div>
                    <div style={{...infoBlockStyle, marginTop: "8px"}}>
                      {t("year")}: {data.session}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Caste Table */}
            <table
              style={{
                width: "100%",
                marginBottom: 12,
                borderCollapse: "collapse",
              }}
            >
              <thead>
                <tr>
                  <th style={tableHeaderStyle} rowSpan={2}>
                    {t("s_no")}
                  </th>
                  <th style={tableHeaderStyle} rowSpan={2}>
                    {t("class")}
                  </th>
                  <th style={tableHeaderStyle} rowSpan={2}>
                    {t("number")}
                  </th>
                  <th style={tableHeaderStyle} rowSpan={2}>
                    {t("total_enrolled_in_class")}
                  </th>
                  {data.casteList.map((caste, idx) => (
                    <th
                      style={tableHeaderStyle}
                      colSpan={3}
                      key={caste.casteName}
                    >
                      {caste.casteName}
                    </th>
                  ))}
                </tr>
                <tr>
                  {data.casteList.map((caste, idx) => (
                    <>
                      <th style={tableHeaderStyle} key={caste.casteName + "-b"}>
                        {t("boys")}
                      </th>
                      <th style={tableHeaderStyle} key={caste.casteName + "-g"}>
                        {t("girls")}
                      </th>
                      <th style={totalHeaderStyle} key={caste.casteName + "-t"}>
                        {t("total")}
                      </th>
                    </>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={tableCellStyle}>1</td>
                  <td style={tableCellStyle}>{data.className} </td>
                  <td style={tableCellStyle}>{t("total_enrolled_in_class")}</td>
                  <td style={tableCellStyle}>{data.totalStudents}</td>
                  {data.casteList.map((caste, idx) => (
                    <React.Fragment key={caste.casteName + "-row"}>
                      <td style={tableCellStyle}>{caste.boysCount}</td>
                      <td style={tableCellStyle}>{caste.girlCount}</td>
                      <td style={tableCellStyle}>{caste.totalCount}</td>
                    </React.Fragment>
                  ))}
                </tr>
                <tr>
                  <td style={tableCellStyle}>2</td>
                  <td style={tableCellStyle}>{data.className} </td>
                  <td style={tableCellStyle}>{t("vocational_subjects")}</td>
                  <td style={tableCellStyle}>{data.vocationalSubjects}</td>
                  {data.casteList.map((caste, idx) => (
                    <React.Fragment key={caste.casteName + "-row2"}>
                      <td style={tableCellStyle}></td>
                      <td style={tableCellStyle}></td>
                      <td style={tableCellStyle}></td>
                    </React.Fragment>
                  ))}
                </tr>
                <tr>
                  <td style={tableCellStyle}>3</td>
                  <td style={tableCellStyle}>{data.className} </td>
                  <td style={tableCellStyle}>{t("other_subjects")}</td>
                  <td style={tableCellStyle}>{data.otherSubjects}</td>
                  {data.casteList.map((caste, idx) => (
                    <React.Fragment key={caste.casteName + "-row3"}>
                      <td style={tableCellStyle}></td>
                      <td style={tableCellStyle}></td>
                      <td style={tableCellStyle}></td>
                    </React.Fragment>
                  ))}
                </tr>
              </tbody>
            </table>

            {/* Subject Table */}
            <div
              style={{
                fontWeight: "bold",
                fontSize: "1.3rem",
                margin: "10px 0 4px 0",
              }}
            >
              {t("subject_wise_enrollment_number")}
            </div>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                marginBottom: 12,
                tableLayout: "fixed",
              }}
            >
              <thead>
                <tr>
                  <th style={{...subjectTableHeader, width: "10%"}}>{t("s_no")}</th>
                  <th style={{...subjectTableHeader, width: "20%"}}>{t("class")}</th>
                  <th style={{...subjectTableHeader, width: "50%"}}>{t("subject")}</th>
                  <th style={{...subjectTableHeader, width: "20%"}}>{t("number")}</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(data.subjects)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([subjectName, count], idx) => {
                    // Get subject code from subjects data
                    const subjectCode = getSubjectCode(subjectName);
                    
                    return (
                      <tr key={subjectName}>
                        <td style={subjectTableCell}>{idx + 1}</td>
                        <td style={subjectTableCell}>{data.className} </td>
                        <td style={subjectTableCell}>
                          {subjectCode ? `${subjectName} (${subjectCode})` : subjectName}
                        </td>
                        <td style={subjectTableCell}>{count}</td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>

          </>
        )}
        </div> {/* End content wrapper */}
      </div>
      
      {/* Download Button - Outside template container */}
      {data && (
        <Box sx={{ textAlign: "center", mb: 2 }}>
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={downloadAsImage}
            disabled={isDownloading}
            sx={{
              backgroundColor: "#3092e3",
              "&:hover": {
                backgroundColor: "primary.main",
              },
            }}
          >
            {isDownloading ? t("generating_image") : t("download_as_image")}
          </Button>
        </Box>
      )}
    </div>
  );
};

export default EnrolmentTemplate;

